import { STROKE_T, WORLD_H, WORLD_W } from '../engine/constants';
import type { Pt } from '../engine/level';
import type { Snapshot } from '../engine/world';

export const COLORS = {
  bg: '#efeff0',
  grid: '#e2eef2',
  obstacle: '#939393',
  stroke: '#7a7c7f',
  strokeInvalid: '#d2d3d5',
  blue: '#3bbded',
  pink: '#ea85ae',
  ink: '#333',
};

/** 꺾인 점열을 중점 2차 곡선으로 부드럽게 잇는다(원을 그려도 각지지 않게). */
function smoothPath(ctx: CanvasRenderingContext2D, pts: Pt[]) {
  if (pts.length < 3) { pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); return; }
  ctx.moveTo(pts[0]![0], pts[0]![1]);
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i]!, b = pts[i + 1]!;
    ctx.quadraticCurveTo(a[0], a[1], (a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
  }
  const last = pts[pts.length - 1]!;
  ctx.lineTo(last[0], last[1]);
}

/** 월드(m, y 위로) ↔ 캔버스 CSS px. 월드 높이를 화면 높이에 맞추고 가로 중앙. */
export class Viewport {
  scale = 1; // px per m
  ox = 0; // 월드 x=0 의 화면 x
  oy = 0; // 월드 y=0(바닥) 의 화면 y
  w = 0; h = 0;

  resize(w: number, h: number) {
    this.w = w; this.h = h;
    const byH = h / WORLD_H, byW = w / WORLD_W;
    this.scale = Math.min(byH, byW);
    this.ox = (w - WORLD_W * this.scale) / 2;
    this.oy = h - (h - WORLD_H * this.scale) / 2;
  }
  toScreen(p: Pt): Pt { return [this.ox + p[0] * this.scale, this.oy - p[1] * this.scale]; }
  toWorld(sx: number, sy: number): Pt { return [(sx - this.ox) / this.scale, (this.oy - sy) / this.scale]; }
}

export class Renderer {
  readonly ctx: CanvasRenderingContext2D;
  readonly vp = new Viewport();
  dpr = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
  }

  resize(w: number, h: number, dpr: number) {
    this.dpr = Math.min(dpr, 2);
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.vp.resize(w, h);
  }

  /** 한 프레임. drawing = 그리는 중인 점열(월드), blocked = 같은 길이의 차단 여부. */
  draw(snap: Snapshot, drawing: { pts: Pt[]; blocked: boolean[]; invalid?: boolean } | null) {
    const { ctx, vp } = this;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, vp.w, vp.h);
    this.grid();

    // 그리기 금지 영역: 그물 무늬
    for (const poly of snap.zones) {
      ctx.save();
      ctx.beginPath();
      poly.forEach((p, i) => { const s = vp.toScreen(p); i ? ctx.lineTo(s[0], s[1]) : ctx.moveTo(s[0], s[1]); });
      ctx.closePath();
      ctx.strokeStyle = '#b9b9b9'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.clip();
      ctx.fillStyle = this.netPattern(); ctx.fill();
      ctx.restore();
    }

    // 탄성 면: 초록 지그재그
    for (const { a, b } of snap.bouncy) {
      const sa = vp.toScreen(a), sb = vp.toScreen(b);
      const len = Math.hypot(sb[0] - sa[0], sb[1] - sa[1]);
      const n = Math.max(2, Math.round(len / 7));
      const amp = 2.5;
      ctx.strokeStyle = '#8fe63c'; ctx.lineWidth = 2; ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const x = sa[0] + (sb[0] - sa[0]) * t, y = sa[1] + (sb[1] - sa[1]) * t;
        const nx = -(sb[1] - sa[1]) / len, ny = (sb[0] - sa[0]) / len;
        const off = (i % 2 ? amp : -amp);
        i ? ctx.lineTo(x + nx * off, y + ny * off) : ctx.moveTo(x + nx * off, y + ny * off);
      }
      ctx.stroke();
    }

    // 얼음(하늘색 상자) — 녹은 것은 안 그림
    for (const i of snap.ice) {
      if (!i.alive) continue;
      const tl = vp.toScreen([i.cx - i.hw, i.cy + i.hh]);
      ctx.fillStyle = '#c9dbe6'; ctx.fillRect(tl[0], tl[1], i.hw * 2 * vp.scale, i.hh * 2 * vp.scale);
      ctx.strokeStyle = '#b3c9d8'; ctx.lineWidth = 1; ctx.strokeRect(tl[0] + 0.5, tl[1] + 0.5, i.hw * 2 * vp.scale - 1, i.hh * 2 * vp.scale - 1);
    }

    // 공이 통과하는 바(연한 회색 + 가운데 ○)
    for (const g of snap.ghosts) {
      const tl = vp.toScreen([g.cx - g.hw, g.cy + g.hh]);
      const w = g.hw * 2 * vp.scale, h = g.hh * 2 * vp.scale;
      ctx.fillStyle = '#d9d9d9'; ctx.fillRect(tl[0], tl[1], w, h);
      const c = vp.toScreen([g.cx, g.cy]);
      ctx.strokeStyle = '#a8a8a8'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(c[0], c[1], Math.max(2, h * 0.28), 0, Math.PI * 2); ctx.stroke();
    }

    // 컨베이어(윤곽선 바 + 양끝 기어). 기어는 dir 방향으로 돈다.
    for (const c of snap.conveyors) {
      const o = vp.toScreen([c.cx, c.cy]);
      const w = c.hw * 2 * vp.scale, h = c.hh * 2 * vp.scale;
      const r = h / 2;
      ctx.save();
      ctx.translate(o[0], o[1]); ctx.rotate(-c.angle);
      ctx.strokeStyle = '#a3a3a3'; ctx.lineWidth = Math.max(1.5, h * 0.18); ctx.fillStyle = '#f4f4f4';
      ctx.beginPath();
      ctx.roundRect(-w / 2, -h / 2, w, h, r);
      ctx.fill(); ctx.stroke();
      const phase = (snap.time * 3 * c.dir) % (Math.PI * 2);
      for (const gx of [-w / 2 + r, w / 2 - r]) {
        ctx.beginPath(); ctx.arc(gx, 0, r * 0.62, 0, Math.PI * 2); ctx.stroke();
        for (let k = 0; k < 6; k++) {
          const a = phase + (k * Math.PI) / 3;
          ctx.beginPath(); ctx.moveTo(gx + Math.cos(a) * r * 0.62, -Math.sin(a) * r * 0.62); ctx.lineTo(gx + Math.cos(a) * r * 0.85, -Math.sin(a) * r * 0.85); ctx.stroke();
        }
      }
      ctx.restore();
    }

    // 움직이는 물체(연한 회색)
    for (const d of snap.dynamics) {
      const o = vp.toScreen(d.pos);
      ctx.save();
      ctx.translate(o[0], o[1]); ctx.rotate(-d.angle); ctx.scale(vp.scale, -vp.scale);
      ctx.fillStyle = '#c4c4c4'; ctx.strokeStyle = '#b0b0b0'; ctx.lineWidth = 1 / vp.scale;
      ctx.beginPath();
      if ('r' in d.shape) ctx.arc(0, 0, d.shape.r, 0, Math.PI * 2);
      else d.shape.pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.restore();
      if (d.pin) {
        // 축 표시: 원작은 풍차는 초록 점, 시소 바는 회색 ○ — 여기서는 작은 회색 고리로 통일
        const s = vp.toScreen(d.pin);
        ctx.strokeStyle = '#8a8a8a'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(s[0], s[1], Math.max(3, 0.07 * vp.scale), 0, Math.PI * 2); ctx.stroke();
      }
    }

    // 장애물(구멍은 evenodd 로 비움)
    ctx.fillStyle = COLORS.obstacle;
    snap.obstacles.forEach((poly, oi) => {
      ctx.beginPath();
      poly.forEach((p, i) => { const s = vp.toScreen(p); i ? ctx.lineTo(s[0], s[1]) : ctx.moveTo(s[0], s[1]); });
      ctx.closePath();
      for (const h of snap.holes[oi] ?? []) {
        h.forEach((p, i) => { const s = vp.toScreen(p); i ? ctx.lineTo(s[0], s[1]) : ctx.moveTo(s[0], s[1]); });
        ctx.closePath();
      }
      ctx.fill('evenodd');
    });

    // 선(강체) — 로컬 점열을 body transform 으로
    ctx.strokeStyle = COLORS.stroke;
    ctx.fillStyle = COLORS.stroke;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.lineWidth = STROKE_T * vp.scale;
    for (const s of snap.strokes) {
      const o = vp.toScreen(s.pos);
      ctx.save();
      ctx.translate(o[0], o[1]);
      ctx.rotate(-s.angle);
      ctx.scale(vp.scale, -vp.scale);
      if (s.segment.kind === 'dot') {
        ctx.beginPath(); ctx.arc(0, 0, STROKE_T / 2, 0, Math.PI * 2); ctx.fill();
      } else {
        ctx.lineWidth = STROKE_T;
        ctx.beginPath();
        smoothPath(ctx, s.local);
        ctx.stroke();
      }
      ctx.restore();
    }

    // 공
    const r = snap.balls.r * vp.scale;
    for (const [c, color] of [[snap.balls.blue, COLORS.blue], [snap.balls.pink, COLORS.pink]] as const) {
      const s = vp.toScreen(c);
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(s[0], s[1], r, 0, Math.PI * 2); ctx.fill();
    }

    // 그리는 중인 선. 공·장애물에 걸린 선은 전체가 연하게(비활성) — 손을 떼면 취소된다
    if (drawing && drawing.pts.length) {
      const color = drawing.invalid ? COLORS.strokeInvalid : COLORS.stroke;
      ctx.strokeStyle = color; ctx.fillStyle = color;
      ctx.lineWidth = STROKE_T * vp.scale;
      ctx.beginPath();
      smoothPath(ctx, drawing.pts.map((p) => vp.toScreen(p)));
      ctx.stroke();
      if (drawing.pts.length === 1) {
        const s = vp.toScreen(drawing.pts[0]!);
        ctx.beginPath(); ctx.arc(s[0], s[1], (STROKE_T / 2) * vp.scale, 0, Math.PI * 2); ctx.fill();
      }
    }
  }

  private pattern: CanvasPattern | null = null;
  /** 원작의 그물 무늬(육각 그물 느낌의 작은 원 격자). */
  private netPattern(): CanvasPattern {
    if (this.pattern) return this.pattern;
    const c = document.createElement('canvas');
    c.width = c.height = 12;
    const g = c.getContext('2d')!;
    g.strokeStyle = '#c8c8c8'; g.lineWidth = 1;
    g.beginPath(); g.arc(6, 6, 5, 0, Math.PI * 2); g.stroke();
    this.pattern = this.ctx.createPattern(c, 'repeat')!;
    return this.pattern;
  }

  private grid() {
    const { ctx, vp } = this;
    const step = 0.048 * WORLD_H * vp.scale; // 정규화 0.048 간격
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = vp.ox % step; x < vp.w; x += step) { ctx.moveTo(Math.round(x) + 0.5, 0); ctx.lineTo(Math.round(x) + 0.5, vp.h); }
    for (let y = vp.oy % step; y < vp.h; y += step) { ctx.moveTo(0, Math.round(y) + 0.5); ctx.lineTo(vp.w, Math.round(y) + 0.5); }
    ctx.stroke();
  }
}
